import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Fade,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { instructorEvaluationApi } from '../../services/instructorEvaluationApi';

const InstructorEvaluations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    loadInstructorEvaluations();
  }, []);

  const loadInstructorEvaluations = async () => {
    setLoading(true);
    try {
      const data = await instructorEvaluationApi.getAllInstructorEvaluations();
      setInstructors(data.instructors || []);
    } catch (err) {
      setError('載入教練評估數據失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (instructor) => {
    try {
      setLoading(true);
      const data = await instructorEvaluationApi.getInstructorEvaluations(instructor.coach_id);
      setSelectedInstructor(data);
      setOpenDialog(true);
    } catch (err) {
      setError('載入教練詳細評估失敗: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInstructor(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 修改：格式化 action_key 顯示為普通文字列表
  const formatActionKey = (actionKey) => {
    if (!actionKey) return '無';
    
    try {
      // 如果是字符串，嘗試解析為 JSON
      let parsedKey = actionKey;
      if (typeof actionKey === 'string') {
        parsedKey = JSON.parse(actionKey);
      }
      
      // 如果是對象或數組，轉換為列表格式
      if (typeof parsedKey === 'object') {
        if (Array.isArray(parsedKey)) {
          // 如果是數組，直接轉換為編號列表
          return parsedKey.map((item, index) => `${index + 1}. ${item}`).join('\n');
        } else {
          // 如果是對象，轉換為鍵值對列表
          return Object.entries(parsedKey)
            .map(([key, value], index) => `${index + 1}. ${key}: ${value}`)
            .join('\n');
        }
      }
      
      // 如果是字符串，按行分割並添加編號
      if (typeof parsedKey === 'string') {
        const lines = parsedKey.split('\n').filter(line => line.trim());
        return lines.map((line, index) => `${index + 1}. ${line.trim()}`).join('\n');
      }
      
      return parsedKey.toString();
    } catch (error) {
      // 如果解析失敗，直接返回原值
      return actionKey.toString();
    }
  };

  // 修改：渲染 action_key 內容
  const renderActionKey = (actionKey) => {
    if (!actionKey) {
      return (
        <Typography variant="body2" color="textSecondary">
          無
        </Typography>
      );
    }

    const formattedKey = formatActionKey(actionKey);
    
    return (
      <Tooltip 
        title={
          <Box sx={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
                lineHeight: 1.5
              }}
            >
              {formattedKey}
            </Typography>
          </Box>
        }
        placement="top"
        arrow
      >
        <Box sx={{ 
          maxWidth: 200, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {formattedKey.length > 50 ? `${formattedKey.substring(0, 50)}...` : formattedKey}
          </Typography>
          <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary' }} />
        </Box>
      </Tooltip>
    );
  };

  // 新增：渲染改善-動作細節內容
  const renderMovementDetails = (movementDetails) => {
    if (!movementDetails || (Array.isArray(movementDetails) && movementDetails.length === 0)) {
      return (
        <Typography variant="body2" color="textSecondary">
          無
        </Typography>
      );
    }

    // 處理陣列格式的動作細節
    let detailsArray = movementDetails;
    if (typeof movementDetails === 'string') {
      try {
        detailsArray = JSON.parse(movementDetails);
      } catch (e) {
        detailsArray = [movementDetails];
      }
    }

    if (!Array.isArray(detailsArray)) {
      detailsArray = [detailsArray];
    }

    const detailsText = detailsArray.join(', ');
    
    return (
      <Tooltip 
        title={detailsText} 
        placement="top"
        arrow
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              maxWidth: 150,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {detailsText.length > 30 ? `${detailsText.substring(0, 30)}...` : detailsText}
          </Typography>
          <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary' }} />
        </Box>
      </Tooltip>
    );
  };

  // 新增：渲染評語內容（與 action_key 相同的邏輯）
  const renderComments = (comments) => {
    if (!comments) {
      return (
        <Typography variant="body2" color="textSecondary">
          無
        </Typography>
      );
    }

    return (
      <Tooltip 
        title={
          <Box sx={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {comments}
            </Typography>
          </Box>
        }
        placement="top"
        arrow
      >
        <Box sx={{ 
          maxWidth: 200, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {comments.length > 50 ? `${comments.substring(0, 50)}...` : comments}
          </Typography>
          <InfoIcon sx={{ ml: 0.5, fontSize: 16, color: 'text.secondary' }} />
        </Box>
      </Tooltip>
    );
  };

  // 創建詳細的 tooltip 內容（參考 StudentEvaluation.jsx）
  const createTooltipContent = (evaluation) => (
    <Box sx={{ maxWidth: 500, p: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {evaluation.student_name}
      </Typography>
      
      {evaluation.target && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>目標：</strong>{evaluation.target}
        </Typography>
      )}
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>環境：</strong>{evaluation.environment}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>裝備：</strong>{evaluation.equipment}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>站姿：</strong>{evaluation.stance}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>問題-階段：</strong>{evaluation.problem_phase}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>問題-表現：</strong>{evaluation.problem_performance}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>問題-動作：</strong>{evaluation.problem_movement}
      </Typography>

      {evaluation.improvement_target && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>改善目標：</strong>{evaluation.improvement_target}
        </Typography>
      )}
      
      {evaluation.improvement_exercise && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>改善-練習：</strong>{evaluation.improvement_exercise}
        </Typography>
      )}
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>改善-動作：</strong>{evaluation.improvement_movement}
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>改善-動作細節：</strong>
      </Typography>
      {Array.isArray(evaluation.improvement_movement_detail) ? (
        evaluation.improvement_movement_detail.map((detail, index) => (
          <Typography key={index} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
            • {detail}
          </Typography>
        ))
      ) : (
        <Typography variant="body2" sx={{ pl: 2 }}>
          {evaluation.improvement_movement_detail || '無'}
        </Typography>
      )}
      
      <Typography variant="body2">
        <strong>評語：</strong>{evaluation.comments}
      </Typography>
    </Box>
  );

  const renderEvaluationCards = (evaluations) => {
    const paginatedData = evaluations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    
    return (
      <Box>
        {paginatedData.map((evaluation) => (
          <Card key={evaluation.id} sx={{ mb: 2, p: isMobile ? 1.5 : 2 }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'stretch' : 'flex-start',
              gap: isMobile ? 1 : 0
            }}>
              <Box sx={{ flex: 1 }}>
                {/* 標題區域 - 學生姓名和創造日期 */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  gap: 1, 
                  mb: 1,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <Typography 
                    variant={isMobile ? "h6" : "h6"} 
                    fontWeight="bold"
                    sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                  >
                    {evaluation.student_name || '未提供'}
                  </Typography>
                  <Chip 
                    label={formatDate(evaluation.created_at)} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      height: isMobile ? 24 : 28
                    }}
                  />
                </Box>
                
                {/* 主要欄位 - 手機版改為單列顯示 */}
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: isMobile ? 1.5 : 2, 
                  mb: 1 
                }}>
                  {evaluation.target && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        目標
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'medium',
                        fontSize: isMobile ? '0.85rem' : '0.875rem',
                        wordBreak: 'break-word'
                      }}>
                        {evaluation.target}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      環境
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.environment}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      裝備
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.equipment}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      站姿
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.stance}
                    </Typography>
                  </Box>
                </Box>
                

                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: isMobile ? 1.5 : 2, 
                  mb: 1 
                }}>  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      問題-階段
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.problem_phase}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      問題-表現
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.problem_performance}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      問題-動作
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.problem_movement}
                    </Typography>
                  </Box>
                
                  {/* 改善目標 */}
                  {evaluation.improvement_target && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        改善目標
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'medium',
                        fontSize: isMobile ? '0.85rem' : '0.875rem',
                        wordBreak: 'break-word'
                      }}>
                        {evaluation.improvement_target}
                      </Typography>
                    </Box>
                  )}

                  {/* 改善-練習 */}
                  {evaluation.improvement_exercise && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        改善-練習
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'medium',
                        fontSize: isMobile ? '0.85rem' : '0.875rem',
                        wordBreak: 'break-word'
                      }}>
                        {evaluation.improvement_exercise}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      改善-動作
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      wordBreak: 'break-word'
                    }}>
                      {evaluation.improvement_movement}
                    </Typography>
                  </Box>
                </Box>
                
                {/* 動作細節 - 手機版優化 */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontSize: isMobile ? '0.7rem' : '0.75rem', 
                    mb: 0.5,
                    fontWeight: 'bold'
                  }}>
                    改善-動作細節
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.5,
                    maxHeight: isMobile ? 60 : 'none',
                    overflow: isMobile ? 'hidden' : 'visible'
                  }}>
                    {Array.isArray(evaluation.improvement_movement_detail) ? (
                      evaluation.improvement_movement_detail.map((detail, index) => (
                        <Chip 
                          key={index}
                          label={detail} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                          sx={{ 
                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                            height: isMobile ? 20 : 24,
                            '& .MuiChip-label': {
                              px: isMobile ? 0.5 : 1
                            }
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{
                        fontSize: isMobile ? '0.85rem' : '0.875rem'
                      }}>
                        {evaluation.improvement_movement_detail || '無'}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                {/* 評語 - 手機版優化 */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    評語
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'medium',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                      maxWidth: isMobile ? '100%' : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: isMobile ? 'normal' : 'nowrap',
                      wordBreak: 'break-word',
                      display: isMobile ? '-webkit-box' : 'block',
                      WebkitLineClamp: isMobile ? 2 : 'none',
                      WebkitBoxOrient: isMobile ? 'vertical' : 'unset'
                    }}
                  >
                    {evaluation.comments}
                  </Typography>
                </Box>
              </Box>
              
              {/* 詳細查看按鈕 - 手機版調整位置 */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: isMobile ? 'center' : 'flex-end',
                mt: isMobile ? 1 : 0
              }}>
                <Tooltip 
                  title={createTooltipContent(evaluation)}
                  placement={isMobile ? "top" : "left"}
                  arrow
                  PopperProps={{
                    style: { zIndex: 9999 },
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          boundary: 'viewport',
                        },
                      },
                      {
                        name: 'flip',
                        enabled: true,
                        options: {
                          fallbackPlacements: isMobile ? ['bottom'] : ['right'],
                        },
                      },
                    ],
                  }}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                        color: 'white',
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        maxWidth: isMobile ? '90vw' : '500px',
                        zIndex: 9999,
                        '& .MuiTooltip-arrow': {
                          color: 'rgba(0, 0, 0, 0.9)',
                        },
                      },
                    },
                  }}
                >
                  <IconButton 
                    size={isMobile ? "medium" : "small"} 
                    color="primary"
                    sx={{
                      backgroundColor: 'primary.light',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                      }
                    }}
                  >
                    <InfoIcon sx={{ fontSize: isMobile ? '1.2rem' : '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Card>
        ))}
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={evaluations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="每頁"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1 : 0
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: isMobile ? '0.8rem' : '0.875rem'
            }
          }}
        />
      </Box>
    );
  };

  const renderMobileCards = () => {
    return (
      <Box>
        {instructors.map((instructor) => (
          <Card key={instructor.coach_id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    {instructor.coach_name}
                  </Typography>
                </Box>
                <Chip 
                  label={`${instructor.total_evaluations} 筆評估`}
                  color="primary"
                  size="small"
                />
              </Box>
              
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  最近評估時間: {instructor.evaluations.length > 0 ? 
                    formatDate(instructor.evaluations[0].created_at) : '無'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  onClick={() => handleViewDetails(instructor)}
                >
                  查看詳情
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const renderDesktopTable = () => {
    return (
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>教練姓名</TableCell>
              <TableCell>評估總數</TableCell>
              <TableCell>最近評估時間</TableCell>
              <TableCell width="120">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {instructors.map((instructor) => (
              <TableRow key={instructor.coach_id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      {instructor.coach_name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${instructor.total_evaluations} 筆`}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {instructor.evaluations.length > 0 ? 
                      formatDate(instructor.evaluations[0].created_at) : '無'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewDetails(instructor)}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 2 }}>
      <Fade in={true}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
            教練評估結果總覽
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Card elevation={1}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                isMobile ? renderMobileCards() : renderDesktopTable()
              )}
            </CardContent>
          </Card>

          {/* 教練詳細評估對話框 */}
          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog} 
            maxWidth="xl" 
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>
              <Box display="flex" alignItems="center">
                <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                {selectedInstructor?.instructor?.username} 的評估記錄
                <Chip 
                  label={`共 ${selectedInstructor?.total_evaluations || 0} 筆`}
                  color="primary"
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              {selectedInstructor && renderEvaluationCards(selectedInstructor.evaluations)}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                關閉
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Fade>
    </Container>
  );
};

export default InstructorEvaluations;
